import { Compass, Download, WifiOff, Share, PlusSquare, X, ArrowLeft, RotateCcw, Lock } from 'lucide-react';
import { usePwaInstall, UsePwaInstallReturn } from '@/hooks/usePwaInstall';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

export interface HeaderProps {
  tripName?: string;
  onBackToDashboard?: () => void;
  showDashboardButton?: boolean;
  onOpenExport?: () => void;
  onResetTrip?: () => void;
  onOpenAdmin?: () => void;
  isAdmin?: boolean;
  viewMode?: 'catalog' | 'map' | 'admin';
  pwaInstall?: UsePwaInstallReturn;
}

export const Header: React.FC<HeaderProps> = ({
  tripName,
  onBackToDashboard,
  showDashboardButton = false,
  onOpenExport,
  onResetTrip,
  onOpenAdmin,
  isAdmin = false,
  pwaInstall: propPwaInstall,
}) => {
  const hookPwaInstall = usePwaInstall();
  const { promptInstall, showIosGuide, setShowIosGuide, isIos, isInstalled } =
    propPwaInstall || hookPwaInstall;
  const { isOnline } = useNetworkStatus();

  return (
    <>
      <header className="min-h-14 pt-[env(safe-area-inset-top,0px)] border-b border-slate-800 bg-slate-900/95 backdrop-blur px-3 sm:px-4 flex items-center justify-between select-none z-40 transition-all">
        {/* Left: Brand Identity & Optional Back Button */}
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
          {showDashboardButton && onBackToDashboard && (
            <button
              type="button"
              onClick={onBackToDashboard}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-slate-800/90 hover:bg-slate-700 border border-slate-700 text-slate-200 hover:text-white text-xs font-semibold transition cursor-pointer active:scale-95 shadow-sm mr-1"
              aria-label="Tutti i Viaggi"
              title="Torna al Catalogo Viaggi"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Tutti i Viaggi</span>
            </button>
          )}

          <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400 shrink-0">
            <Compass className="w-5 h-5 animate-[spin_25s_linear_infinite]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xs sm:text-sm font-bold text-slate-100 truncate">
              Ibasho
            </h1>
            <p className="text-[10px] sm:text-[11px] text-slate-400 truncate hidden xs:block">
              {tripName || 'Mappe & Fotografie di Viaggio'}
            </p>
          </div>
        </div>

        {/* Right: Actions, Offline Badge, PWA Install */}
        <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
          {/* Offline Status Badge */}
          {!isOnline && (
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-medium animate-pulse"
              role="status"
              aria-live="polite"
              title="Dispositivo offline. Mappa e foto già caricate restano accessibili."
            >
              <WifiOff className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span className="text-[11px] font-semibold">Offline</span>
            </div>
          )}

          {/* New Trip / Reset Button */}
          {onResetTrip && (
            <button
              type="button"
              onClick={onResetTrip}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-rose-950/40 hover:bg-rose-900/60 text-rose-200 hover:text-white rounded-xl text-xs font-semibold border border-rose-700/60 transition shadow-sm cursor-pointer"
              title="Azzera dati e inizia un nuovo viaggio"
            >
              <RotateCcw className="w-3.5 h-3.5 text-rose-400" />
              <span>Nuovo Viaggio</span>
            </button>
          )}

          {/* Export ZIP Package Button */}
          {onOpenExport && (
            <button
              type="button"
              onClick={onOpenExport}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-xl text-xs font-medium border border-slate-700 transition cursor-pointer"
              title="Esporta archivio ZIP autonomo con visualizzatore offline"
            >
              <Download className="w-3.5 h-3.5 text-sky-400" />
              <span className="hidden sm:inline">Esporta ZIP</span>
            </button>
          )}

          {/* PWA Install Button */}
          {!isInstalled && (
            <button
              type="button"
              onClick={promptInstall}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-500 hover:to-blue-500 active:scale-95 text-white rounded-xl text-xs font-semibold shadow-md shadow-sky-500/20 border border-sky-400/30 transition cursor-pointer"
              aria-label="Installa applicazione su schermata Home"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Installa App</span>
              <span className="sm:hidden text-[11px]">Installa</span>
            </button>
          )}



          {/* Discreet Admin Mode Trigger */}
          {onOpenAdmin && !isAdmin && (
            <button
              type="button"
              onClick={onOpenAdmin}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-slate-800/50 hover:bg-slate-800 text-slate-400 hover:text-slate-200 text-xs font-medium border border-slate-700/50 hover:border-slate-600 transition cursor-pointer"
              title="Pannello Amministratore (Caricamento Dati & Reset)"
              aria-label="Pannello Amministratore"
            >
              <Lock className="w-3 h-3 text-slate-400" />
              <span className="hidden sm:inline">Admin</span>
            </button>
          )}
        </div>
      </header>

      {/* iOS Safari "Aggiungi alla schermata Home" Modal */}
      {showIosGuide && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-2xl text-slate-100 flex flex-col gap-4 relative animate-in slide-in-from-bottom-6 duration-200">
            <button
              type="button"
              onClick={() => setShowIosGuide(false)}
              className="absolute top-3.5 right-3.5 w-7 h-7 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition"
              aria-label="Chiudi guida"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500/20 border border-sky-500/30 flex items-center justify-center text-sky-400">
                <Compass className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Installa Ibasho</h3>
                <p className="text-xs text-slate-400">
                  {isIos ? 'Aggiungi alla schermata Home di iOS' : 'Applicazione Web Autonoma & Offline'}
                </p>
              </div>
            </div>

            {isIos ? (
              <ol className="text-xs text-slate-300 space-y-2.5 bg-slate-800/50 p-3.5 rounded-xl border border-slate-700/50">
                <li className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 font-bold flex items-center justify-center shrink-0 text-[11px]">
                    1
                  </span>
                  <span>
                    Tocca il pulsante <strong className="text-white">Condividi</strong> (<Share className="w-3.5 h-3.5 inline mx-0.5 text-sky-400" />) nella barra di Safari in basso.
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 font-bold flex items-center justify-center shrink-0 text-[11px]">
                    2
                  </span>
                  <span>
                    Scorri e seleziona <strong className="text-white">Aggiungi alla schermata Home</strong> (<PlusSquare className="w-3.5 h-3.5 inline mx-0.5 text-sky-400" />).
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 font-bold flex items-center justify-center shrink-0 text-[11px]">
                    3
                  </span>
                  <span>Tocca <strong className="text-white">Aggiungi</strong> in alto a destra per completare.</span>
                </li>
              </ol>
            ) : (
              <ol className="text-xs text-slate-300 space-y-2.5 bg-slate-800/50 p-3.5 rounded-xl border border-slate-700/50">
                <li className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 font-bold flex items-center justify-center shrink-0 text-[11px]">
                    1
                  </span>
                  <span>
                    Clicca sull'icona <strong className="text-white">Installa</strong> (<Download className="w-3.5 h-3.5 inline mx-0.5 text-sky-400" />) nella barra degli indirizzi in alto a destra.
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 font-bold flex items-center justify-center shrink-0 text-[11px]">
                    2
                  </span>
                  <span>
                    In alternativa, apri il menu del browser (<strong className="text-white">&#8942;</strong>) e scegli <strong className="text-white">Installa applicazione</strong>.
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 font-bold flex items-center justify-center shrink-0 text-[11px]">
                    3
                  </span>
                  <span>Avviala per consultare mappe, itinerari e fotografie anche completamente offline!</span>
                </li>
              </ol>
            )}

            <button
              type="button"
              onClick={() => setShowIosGuide(false)}
              className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl transition cursor-pointer"
            >
              Ho capito
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
